/**
 * Modal de prévia da proposta antes do envio (PR-M1-01c-ii.b —
 * pedido do Alexandre: "antes de enviar a proposta, quero ter a opção
 * de visualizar a página html da proposta antes de enviar").
 *
 * Usa o mesmo componente `ProposalPublicView` que a rota pública
 * `/proposta/:token` vai renderizar — assim a prévia é fiel ao que o
 * lead verá. mode="preview" esconde os botões Aceitar/Dúvida.
 *
 * Footer tem "Fechar" e opcionalmente "Enviar proposta" quando vem do
 * card de rascunho — atalho pra enviar direto após aprovar a prévia.
 */
import { useState, type ReactNode } from 'react';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Skeleton,
  toast,
} from '@kmgestao/ui';

import {
  ProposalPublicView,
  useSendProposal,
  useUpdateProposalDraft,
  usePlatformSettings,
  type LeadProposalSummary,
} from '../../../features/proposals/index.js';
import { copyToClipboard } from '../../../lib/clipboard.js';

export interface ProposalPreviewModalProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  proposal: LeadProposalSummary;
  lead: { id: string; full_name: string; clinic_name_guess: string | null };
  /** Se `true`, mostra botão "Enviar agora" no footer (atalho pro envio
   * imediato após aprovar a prévia). Só aparece quando proposal.status
   * === 'draft'. */
  showSendAction?: boolean;
  /** Callback "Editar" — quando fornecido, renderiza botão no footer
   * que fecha este modal e aciona o editor. Só aparece em draft. */
  onEdit?: (() => void) | undefined;
}

export function ProposalPreviewModal({
  open,
  onOpenChange,
  proposal,
  lead,
  showSendAction = false,
  onEdit,
}: ProposalPreviewModalProps) {
  const settings = usePlatformSettings();
  const sendProposal = useSendProposal();
  const updateProposal = useUpdateProposalDraft();
  const [confirmSend, setConfirmSend] = useState(false);

  const isDraft = proposal.status === 'draft';
  // PR-M1-01c-ii.i (2026-04-24): amplia edição pra `sent`/`viewed`
  // também (Karla conversando via WhatsApp pode querer reajustar).
  const isEditable =
    isDraft ||
    proposal.status === 'sent' ||
    proposal.status === 'viewed' ||
    proposal.status === 'in_negotiation';
  const canSend = showSendAction && isEditable;
  const canEdit = isEditable && typeof onEdit === 'function';

  // Inline edit de notas (PR-M1-01c-ii.e, 2026-04-24): Karla clica "Editar
  // escopo" dentro da prévia e o texto vira textarea. Save dispara
  // useUpdateProposalDraft.
  const handleSaveNotes = async (nextNotes: string) => {
    if (!isEditable) return;
    try {
      await updateProposal.mutateAsync({
        id: proposal.id,
        patch: { notes: nextNotes },
        lead_id: lead.id,
      });
      toast.success('Escopo atualizado.');
    } catch (err) {
      toast.error('Falha ao salvar escopo', {
        description: (err as Error).message,
      });
      throw err; // re-throw pra ProposalPublicView manter modo edição aberto
    }
  };

  const handleSend = () => {
    sendProposal.mutate(
      { proposal_id: proposal.id, lead_id: lead.id },
      {
        onSuccess: async (res) => {
          setConfirmSend(false);
          if (res.email_status === 'sent') {
            toast.success('Proposta enviada por email.');
          } else if (res.email_status === 'skipped') {
            toast.info('Proposta marcada como enviada (modo dev).');
          } else {
            toast.warning('Proposta enviada, mas o email falhou — copie o link manual.');
          }
          const copied = await copyToClipboard(res.public_url);
          if (copied) toast.info('Link público copiado para a área de transferência.');
          onOpenChange(false);
        },
        onError: (err) =>
          toast.error('Falha ao enviar proposta', {
            description: (err as Error).message,
          }),
      },
    );
  };

  let body: ReactNode;
  if (settings.isLoading) {
    body = <Skeleton className="h-[60vh] w-full" />;
  } else if (settings.isError || !settings.data) {
    body = (
      <div
        className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive"
        role="alert"
      >
        Não foi possível carregar as configurações da plataforma.{' '}
        {settings.error instanceof Error ? settings.error.message : null}
      </div>
    );
  } else {
    body = (
      <ProposalPublicView
        proposal={proposal}
        platformSettings={settings.data}
        lead={{ full_name: lead.full_name, clinic_name_guess: lead.clinic_name_guess }}
        mode="preview"
        onSaveNotes={isEditable ? handleSaveNotes : undefined}
        isSavingNotes={updateProposal.isPending}
      />
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) setConfirmSend(false);
      }}
    >
      <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>Prévia da proposta</DialogTitle>
          <DialogDescription>
            Esta é exatamente a tela que {lead.full_name} verá ao abrir o link público. Se precisar
            de ajustes, clique em{' '}
            {canEdit ? (
              <button
                type="button"
                onClick={() => {
                  onOpenChange(false);
                  onEdit?.();
                }}
                className="inline font-semibold text-accent underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                Editar
              </button>
            ) : (
              <span className="font-semibold">Editar</span>
            )}{' '}
            para voltar ao formulário antes de enviar.
          </DialogDescription>
        </DialogHeader>

        <div className="-mx-4 border-y border-border bg-background sm:-mx-6">{body}</div>

        <DialogFooter className="gap-2">
          <DialogClose asChild>
            <Button type="button" variant="outline" size="lg">
              Fechar
            </Button>
          </DialogClose>
          {canEdit ? (
            <Button
              type="button"
              variant="outline"
              size="lg"
              onClick={() => {
                onOpenChange(false);
                onEdit?.();
              }}
              disabled={sendProposal.isPending}
            >
              Editar rascunho
            </Button>
          ) : null}
          {canSend ? (
            confirmSend ? (
              <>
                <Button size="lg" onClick={handleSend} disabled={sendProposal.isPending}>
                  {sendProposal.isPending ? 'Enviando…' : 'Confirmar envio'}
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => setConfirmSend(false)}
                  disabled={sendProposal.isPending}
                >
                  Cancelar
                </Button>
              </>
            ) : (
              <Button size="lg" onClick={() => setConfirmSend(true)}>
                Enviar agora
              </Button>
            )
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
